/**
 * Duplicate routes that should consolidate their ranking signals onto a single
 * canonical URL.
 *
 * The /tools/* name-generator and lyric-video pages are older, thin duplicates
 * of the richer top-level tool pages (same tool, two URLs). We keep them live
 * for users but point rel=canonical at the authoritative twin so Google and the
 * AI answer engines index and rank ONE page instead of splitting signals across
 * two. Applied on BOTH sides so the crawler-served HTML and the JS-rendered DOM
 * agree: server/_core/seo.ts (toCanonicalPath) and client useSEO
 * (normalizeSeoPath). Keys and values are language-agnostic base paths.
 */
export const CANONICAL_CONSOLIDATION: Record<string, string> = {
  "/tools/band-name-generator": "/band-name-generator",
  "/tools/artist-name-generator": "/artist-name-generator",
  "/tools/album-name-generator": "/album-name-generator",
  "/tools/song-name-generator": "/song-title-generator",
  "/tools/lyric-video-generator": "/lyric-video-generator",
  // /lyrics-video-generator serves the same component and the same copy as
  // /lyric-video-generator, and declared itself canonical. Search Console
  // (URL inspection, Sep 28 2026) files it as "Duplicate, Google chose
  // different canonical than user" with /lyric-video-generator as Google's
  // pick, so the tag now says what Google already decided. The /tools/ twin
  // points straight at the same target rather than through a second hop.
  "/lyrics-video-generator": "/lyric-video-generator",
  "/tools/lyrics-video-generator": "/lyric-video-generator",
  "/tools/ai-singing-video-generator": "/ai-singing-video-generator",
  // Shorter alias of the same tool; consolidate onto the "ai-" head term.
  "/singing-video-generator": "/ai-singing-video-generator",
  // Dance video: one product page, three search intents ("dance video
  // generator", "ai dance generator", "make photo dance").
  "/dance-video-generator": "/ai-dance-video-generator",
  "/ai-dance-generator": "/ai-dance-video-generator",
  "/tools/ai-dance-video-generator": "/ai-dance-video-generator",
};

/** Return the canonical base path for a route, collapsing known duplicates. */
export function consolidateCanonicalPath(basePath: string): string {
  return CANONICAL_CONSOLIDATION[basePath] ?? basePath;
}
